/**
 * Pure selectors over the in-memory annotation list.
 *
 * Counts are always derived — never hardcoded in tables or tab badges.
 */

export function isConversationContext(context) {
  return !context || context.type === 'conversation';
}

export function isRunContext(context, runId) {
  return context?.type === 'run' && String(context.id) === String(runId);
}

export function isSpanContext(context, spanId, startedAt) {
  if (context?.type !== 'span' || context.id !== spanId) return false;
  if (startedAt && context.startedAt) {
    const a = new Date(context.startedAt).getTime();
    const b = new Date(startedAt).getTime();
    if (Number.isFinite(a) && Number.isFinite(b)) return a === b;
    return context.startedAt === startedAt;
  }
  return true;
}

export function listConversationAnnotations(annotations, conversationId) {
  if (!conversationId) return [];
  return annotations.filter((a) => String(a.conversationId) === String(conversationId));
}

export function belongsToRun(annotation, runId) {
  if (!runId || !annotation) return false;
  if (isRunContext(annotation.context, runId)) return true;
  if (annotation.context?.type === 'span' && String(annotation.context.runId || '') === String(runId)) {
    return true;
  }
  return false;
}

export function listRunAnnotations(annotations, runId) {
  if (!runId) return [];
  return annotations.filter((a) => belongsToRun(a, runId));
}

export function listSpanAnnotations(annotations, spanId, startedAt) {
  if (!spanId) return [];
  return annotations.filter((a) => isSpanContext(a.context, spanId, startedAt));
}

export function getConversationAnnotationCount(annotations, conversationId) {
  return listConversationAnnotations(annotations, conversationId).length;
}

export function getRunAnnotationCount(annotations, runId) {
  return listRunAnnotations(annotations, runId).length;
}

export function getSpanAnnotationCount(annotations, spanId, startedAt) {
  return listSpanAnnotations(annotations, spanId, startedAt).length;
}

export function sortAnnotations(items) {
  return [...items].sort((a, b) => {
    const ta = new Date(a.createdAt).getTime();
    const tb = new Date(b.createdAt).getTime();
    return tb - ta;
  });
}

export function spanCountKey(spanId, startedAt) {
  const started = startedAt ? new Date(startedAt).toISOString() : '';
  return `${spanId}|${started}`;
}

/**
 * Login stores the session user at localStorage.User ({ id, email, name }).
 * `userId` is a stale key that is not written on login.
 */
export function resolveStoredUser(storageLike) {
  const store = storageLike || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!store || typeof store.getItem !== 'function') {
    return { id: null, email: null, name: null };
  }
  try {
    const raw = store.getItem('User');
    if (raw) {
      const u = JSON.parse(raw);
      const n = Number(u?.id ?? u?.userId);
      const email = typeof u?.email === 'string' ? u.email.trim().toLowerCase() : '';
      return {
        id: Number.isFinite(n) && n > 0 ? n : null,
        email: email || null,
        name: u?.name || null,
      };
    }
  } catch {
    // fall through to legacy key
  }
  const legacy = Number.parseInt(store.getItem('userId'), 10);
  return {
    id: Number.isFinite(legacy) && legacy > 0 ? legacy : null,
    email: null,
    name: null,
  };
}

function selfEmailOf(value) {
  return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : '';
}

/**
 * Autocomplete roster: other team members only.
 * Exclusion is by authenticated user id and email (not display name) so
 * searching your own name cannot bring you back into the dropdown.
 */
export function filterMentionSuggestions(users, query, currentUserId, currentUserEmail) {
  const list = Array.isArray(users) ? users : [];
  const q = String(query || '').toLowerCase();
  const selfId = currentUserId == null || currentUserId === ''
    ? null
    : Number(currentUserId);
  const hasSelf = selfId != null && Number.isFinite(selfId);
  const selfEmail = selfEmailOf(currentUserEmail);
  const seen = new Set();
  return list.filter((u) => {
    if (!u) return false;
    const uid = Number(u.id);
    if (Number.isInteger(uid) && uid > 0) {
      if (seen.has(uid)) return false;
      seen.add(uid);
    }
    if (hasSelf && uid === selfId) return false;
    if (selfEmail && selfEmailOf(u.email) === selfEmail) return false;
    if (!q) return true;
    return String(u.name || '').toLowerCase().includes(q);
  });
}
